import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { recordActivitySignal } from '../activity/signals.js';
import type { EntityDetail } from '../api/client.js';
import {
  areMinimapDimensionsEqual,
  getInspectionStepIdFromPlayerAction,
  initialPlayerInspectionState,
  MINIMAP_TOP_PADDING,
  normalizeMinimapDimensions,
  reducePlayerInspectionState,
  type MinimapDimensions,
} from '../lib/playerMinimap.js';
import { PLAYER_IGNORE_REQUESTS, serializeContentUnit } from '../lib/contentSerializer.js';
import { renderContentText } from '../lib/flowchart.js';
import { PLAYER_BASE, PLAYER_SCRIPT, PLAYER_STYLE } from '../lib/playerAssets.js';

const PLAYER_OVERRIDE_STYLE_ID = 'player-preview-overrides';
const PLAYER_OVERRIDE_CSS = `
  /* Carve the player canvas out of Tailwind preflight. The player stylesheet lives in
     the lowest cascade layer (see the layer-order style), so preflight's universal
     margin/padding/border reset in \`base\` beats every player rule by layer order —
     that flattened the whole canvas (2026-07-23 spacing regression). Inside \`base\`,
     this ID-scoped selector outweighs preflight's zero-specificity resets, and
     revert-layer re-resolves each property against the layers BELOW base — i.e. the
     player stylesheet — as if preflight did not exist inside the canvas. box-sizing
     is re-asserted because the canvas has always rendered border-box and the player
     bundle mostly assumes its host sets it. Form controls and ::placeholder get the
     full revert too — @tailwindcss/forms styles them well beyond spacing (radius,
     checkbox sizing, select arrow) and the player must win on all of it. Only
     replaced media elements keep preflight's load-bearing guards (max-width:100%,
     display:block) and revert just the properties the player actually styles.
     App styles outside the canvas are untouched: the layer order still keeps every
     player global below base/components/utilities (S3-T7/S4-T8 must not regress). */
  @layer base {
    #player-preview-root :not(img, video, svg, canvas, audio, iframe),
    #player-preview-root ::before,
    #player-preview-root ::after,
    #player-preview-root ::placeholder {
      all: revert-layer;
      box-sizing: border-box;
    }
    #player-preview-root :is(img, video, svg, canvas, audio, iframe) {
      margin: revert-layer;
      padding: revert-layer;
      border: revert-layer;
      color: revert-layer;
      background-color: revert-layer;
      opacity: revert-layer;
    }
  }
  #player-preview-root [class*="player__minimap-open-button_button"] {
    display: none !important;
  }
  #player-preview-root [class*="player__content-addon-open-button_button"] {
    display: none !important;
  }
  #player-preview-root[data-player-editable="true"] [class*="player__editable-step-actions_container"] {
    display: flex !important;
    opacity: 0.72 !important;
    transition: opacity 120ms ease !important;
  }
  #player-preview-root[data-player-editable="true"] [class*="player__editable-step-actions_container"]:hover,
  #player-preview-root[data-player-editable="true"] [class*="player__editable-step-actions_container"]:focus-within {
    opacity: 1 !important;
  }
  #player-preview-root [class*="player__minimap_buttonClose"] {
    display: none !important;
  }
  #player-preview-root [class*="player__minimap_buttonZoomIn"] {
    display: none !important;
  }
  #player-preview-root [class*="player__minimap_buttonZoomOut"] {
    display: none !important;
  }
  #player-preview-root [class*="player__player-layout_container"] {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) !important;
    grid-template-rows: minmax(0, 1fr) var(--author-minimap-height, clamp(220px, 20vh, 320px)) !important;
    height: 100% !important;
  }
  #player-preview-root [class*="player__player-layout_contentMiddle"] {
    grid-row: 1 !important;
    grid-column: 1 !important;
    min-width: 0 !important;
    width: 100% !important;
  }
  #player-preview-root [class*="player__player-layout_contentLeft"] {
    grid-row: 2 !important;
    grid-column: 1 !important;
    width: 100% !important;
    height: var(--author-minimap-height, clamp(220px, 20vh, 320px)) !important;
    min-height: 0 !important;
    overflow: hidden !important;
    border-right: none !important;
    border-top: 1px solid var(--ui-skin-border) !important;
  }
  #player-preview-root [class*="player__player-layout_contentRight"] {
    display: none !important;
  }
  #player-preview-root [class*="player__player-layout-column_container"] {
    width: 100% !important;
    height: 100% !important;
    min-height: 0 !important;
  }
  #player-preview-root [class*="player__player-layout-block_container"] {
    height: 100% !important;
  }
  #player-preview-root [class*="player__player-layout_content--"] {
    min-width: 0 !important;
    overflow: hidden !important;
  }
  #player-preview-root [class*="player__player-layout_contentWrapper"] {
    height: 100% !important;
    padding-bottom: 32px !important;
  }
  #player-preview-root[data-player-review-workflow="true"] [class*="player__player-layout_contentWrapper"] {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }
  #player-preview-root [class*="player__player-layout_contentMain"] {
    min-height: 0 !important;
  }
  #player-preview-root [class*="player__minimap_container"] {
    height: 100% !important;
    min-height: 0 !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
  }
  /* Review columns can grow beyond the vendor's centred 680/790px prose caps. Full graph text
     still comes from StepExpansion, because the vendor truncates it before rendering. */
  #player-preview-root[data-player-review-workflow="true"] [class*="player__step-instance_container"],
  #player-preview-root[data-player-review-workflow="true"] [class*="player__editable-step-instance_container"] {
    max-width: 100% !important;
  }
  #player-preview-root[data-player-review-workflow="true"] [class*="player__minimap_container"] {
    overflow-y: auto !important;
  }
  #player-preview-root [class*="player__minimap_container"]:has([data-author-workflow-details]) {
    overflow-y: auto !important;
  }
  #player-preview-root[data-player-review-workflow="true"] [class*="player__minimap_container"] > :first-child,
  #player-preview-root [class*="player__minimap_container"]:has([data-author-workflow-details]) > :first-child {
    overflow: auto !important;
  }
  #player-preview-root [class*="player__minimap_container"]:has([data-author-workflow-details]) > :first-child > :first-child {
    overflow: auto !important;
  }
  /* Review panels consume their full measured height. The fixed review pane scrolls vertically;
     only the vendor's innermost viewport scrolls horizontally, so every graph column is reachable. */
  #player-preview-root[data-player-review-workflow="true"] [class*="player__player-layout_contentLeft"] {
    padding-top: var(--author-workflow-controls-height, 0px) !important;
  }
  #player-preview-root[data-workflow-content-height="natural"] [class*="player__minimap_container"],
  #player-preview-root[data-workflow-content-height="natural"] [class*="player__minimap_container"] > :first-child {
    overflow: hidden !important;
  }
  #player-preview-root[data-workflow-content-height="natural"] [class*="player__minimap_container"] > :first-child > :first-child {
    overflow-x: auto !important;
    overflow-y: hidden !important;
  }
  /* Highlight the native routing card, but never change its width/height or hide its prompt.
     Vendor answer connectors only measure on component updates, so changing card geometry can
     leave stale arrows. Full text lives in a separate aligned layer above the compact graph. */
  #player-preview-root [class*="player__minimap-step_container"][data-author-step-expanded="true"] {
    outline: 2px solid rgba(180, 83, 9, 0.9) !important;
    outline-offset: 2px !important;
  }
  #player-preview-root [data-author-workflow-details] {
    user-select: text !important;
  }
  /* Active / current step highlight inside the minimap.
     The player uses BEM modifier classes; we cover several naming conventions. */
  #player-preview-root [class*="player__minimap_levels"] [class*="--active"],
  #player-preview-root [class*="player__minimap_levels"] [class*="__active"],
  #player-preview-root [class*="player__minimap_levels"] [class*="_active_"],
  #player-preview-root [class*="player__minimap_levels"] [class*="_current"] {
    outline: 2.5px solid rgba(22, 39, 31, 0.85) !important;
    outline-offset: 3px !important;
    border-radius: 8px !important;
  }
  @media (min-width: 1280px) {
    #player-preview-root [class*="player__player-layout_container"] {
      grid-template-columns: var(--author-ai-pane-width, calc(clamp(19rem, 23vw, 23rem) + 1rem)) minmax(0, 1fr) !important;
      grid-template-rows: minmax(0, 1fr) var(--author-minimap-height, clamp(220px, 20vh, 320px)) !important;
    }
    #player-preview-root [class*="player__player-layout_contentMiddle"] {
      grid-column: 2 !important;
      grid-row: 1 !important;
    }
    #player-preview-root [class*="player__player-layout_contentLeft"] {
      grid-column: 1 / -1 !important;
      grid-row: 2 !important;
      height: var(--author-minimap-height, clamp(220px, 20vh, 320px)) !important;
      overflow: hidden !important;
      border-top: 1px solid var(--ui-skin-border) !important;
    }
  }
  /* Learner preview: no flowchart/minimap strip — collapse the reserved bottom row.
     Attribute selector outranks the unscoped !important rules above at any width. */
  #player-preview-root[data-player-minimap="hidden"] [class*="player__player-layout_container"] {
    grid-template-rows: minmax(0, 1fr) !important;
  }
  #player-preview-root[data-player-minimap="hidden"] [class*="player__player-layout_contentLeft"] {
    display: none !important;
  }
`;

const PLAYER_SHOW_MINIMAP = '@PLAYER/SHOW_MINIMAP';
const PLAYER_UPDATE_MINIMAP = '@PLAYER/UPDATE_MINIMAP';
const MINIMAP_HEIGHT_STORAGE_KEY = 'author.review.minimap-height';
/**
 * Whether the reviewer has collapsed the workflow graph (AD intake 2026-08-06).
 *
 * The graph was permanently open in Review & edit mode: we dispatch `@PLAYER/SHOW_MINIMAP` on
 * every editable render, the override CSS hides the player's own open *and* close buttons, and the
 * resizer floors at `MINIMAP_HEIGHT_MIN`. A reviewer on a tall screen therefore lost up to 320px of
 * reading height to a strip whose cards truncate by design, with no way to reclaim it short of
 * Learner preview — which also turns editing off. Session-scoped, like the height beside it: a
 * collapse is a per-sitting working preference, not a durable setting.
 */
const GRAPH_COLLAPSED_STORAGE_KEY = 'author.review.graph-collapsed';
const MINIMAP_HEIGHT_MIN = 160;
const MINIMAP_HEIGHT_MAX_RATIO = 0.75;
const MINIMAP_HEIGHT_STEP = 32;
const MINIMAP_HEIGHT_DEFAULT_MIN = 210;
const MINIMAP_HEIGHT_DEFAULT_MAX = 320;
const MINIMAP_HEIGHT_DEFAULT_CSS = 'clamp(210px, 20vh, 320px)';
/**
 * How tall the graph strip becomes while a step card is expanded.
 *
 * Sized from the content: a step prompt plus its options is a few hundred pixels, and the resting
 * strip is 210–320px, most of which the card's own chrome takes. Still clamped by
 * `maxMinimapHeight` (75% of the player), so the item never disappears behind the graph.
 */
const MINIMAP_HEIGHT_EXPANDED_MIN = 460;

const SCROLL_MARGIN = 32;

/**
 * Finds the currently active step node inside the minimap and scrolls it
 * horizontally (and vertically if needed) into the visible portion of the
 * minimap container.  We rely on the player applying a BEM modifier class
 * such as `--active`, `--current`, or `_active` to the active card element.
 */
function activeMinimapNode(root: HTMLElement): HTMLElement | null {
  const levels = root.querySelector('[class*="player__minimap_levels"]');
  return [...levels?.querySelectorAll<HTMLElement>(
    '[class*="--active"],[class*="__active"],[class*="_active_"],[class*="_current"]',
  ) ?? []].find((node) => !node.closest('[data-author-step-host]')) ?? null;
}

function scrollActiveNodeIntoView(root: HTMLElement): void {
  const minimap = root.querySelector<HTMLElement>('[class*="player__minimap_container"]');
  if (!minimap) return;

  const levels = minimap.querySelector<HTMLElement>('[class*="player__minimap_levels"]');
  if (!levels) return;

  // Try common BEM active/current modifier patterns.
  const active = activeMinimapNode(root);
  if (!active) return;

  // MapInteraction wraps its clipping viewport in another full-size div. Expanded details make
  // that inner viewport scrollable; older mounts/tests may scroll the outer one instead.
  const scroller = [minimap.firstElementChild?.firstElementChild, minimap.firstElementChild]
    .find((viewport): viewport is HTMLElement => {
      if (!(viewport instanceof HTMLElement)) return false;
      const css = getComputedStyle(viewport);
      return [css.overflowX, css.overflowY, css.overflow].some((value) => /auto|scroll/.test(value))
        && (viewport.scrollWidth > viewport.clientWidth || viewport.scrollHeight > viewport.clientHeight);
    }) ?? minimap;
  const containerRect = scroller.getBoundingClientRect();
  const nodeRect = active.getBoundingClientRect();

  // Horizontal scroll
  const nodeRelLeft = nodeRect.left - containerRect.left + scroller.scrollLeft;
  const nodeRelRight = nodeRelLeft + nodeRect.width;
  const visibleLeft = scroller.scrollLeft;
  const visibleRight = visibleLeft + scroller.clientWidth;

  if (nodeRect.width > scroller.clientWidth - 2 * SCROLL_MARGIN || nodeRelLeft - SCROLL_MARGIN < visibleLeft) {
    scroller.scrollLeft = Math.max(0, nodeRelLeft - SCROLL_MARGIN);
  } else if (nodeRelRight + SCROLL_MARGIN > visibleRight) {
    scroller.scrollLeft = nodeRelRight + SCROLL_MARGIN - scroller.clientWidth;
  }

  // Vertical scroll (only relevant when content overflows downward due to zoom)
  const nodeRelTop = nodeRect.top - containerRect.top + scroller.scrollTop;
  const nodeRelBottom = nodeRelTop + nodeRect.height;
  const visibleTop = scroller.scrollTop;
  const visibleBottom = visibleTop + scroller.clientHeight;

  if (nodeRelTop - SCROLL_MARGIN < visibleTop) {
    scroller.scrollTop = Math.max(0, nodeRelTop - SCROLL_MARGIN);
  } else if (nodeRelBottom + SCROLL_MARGIN > visibleBottom) {
    scroller.scrollTop = nodeRelBottom + SCROLL_MARGIN - scroller.clientHeight;
  }
}

declare global {
  interface Window {
    MindojoPlayer?: new (config: Record<string, unknown>) => PlayerInstance;
  }
}

interface PlayerStoreState {
  minimap?: {
    isShown?: boolean;
    mapDimensions?: MinimapDimensions;
  };
}

interface PlayerStore {
  dispatch: (action: unknown) => unknown;
  getState: () => PlayerStoreState;
  subscribe: (listener: () => void) => () => void;
}

interface PlayerInstance {
  destroy?: () => void;
  render?: () => void;
  store?: PlayerStore;
}

export function PlayerPreview({
  entity,
  mode = 'editable',
  reserveAiPaneWidth,
  saveNamespace,
  expandWorkflow = false,
}: {
  entity: EntityDetail | null;
  mode?: 'editable' | 'vanilla';
  reserveAiPaneWidth?: boolean;
  saveNamespace?: string;
  expandWorkflow?: boolean;
}) {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const workflowControlsRef = useRef<HTMLDivElement | null>(null);
  const [workflowControlsHeight, setWorkflowControlsHeight] = useState(40);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<PlayerInstance | null>(null);
  const pendingResizeRef = useRef(false);
  const minimapSyncRef = useRef<() => void>(() => undefined);
  const dragRef = useRef<{ pointerId: number; startY: number; startHeight: number } | null>(null);
  const [bundleStatus, setBundleStatus] = useState<'idle' | 'ready' | 'error'>('idle');
  const [preferredMinimapHeight, setPreferredMinimapHeight] = useState<number | null>(readStoredMinimapHeight);
  const [graphCollapsed, setGraphCollapsed] = useState(() => expandWorkflow ? false : readStoredGraphCollapsed());
  const [workflowDefaultExpanded, setWorkflowDefaultExpanded] = useState(true);
  const [workflowOverrides, setWorkflowOverrides] = useState<Map<number, boolean>>(() => new Map());
  const workflowActionRef = useRef<(stepId: number, action: unknown) => void>(() => undefined);
  const manuallyOpenedSteps = useRef(new Set<number>());
  const [defaultMinimapHeight, setDefaultMinimapHeight] = useState(resolveDefaultMinimapHeight);
  const [maxMinimapHeight, setMaxMinimapHeight] = useState(MINIMAP_HEIGHT_DEFAULT_MAX);
  const [inspection, dispatchInspection] = useReducer(
    reducePlayerInspectionState,
    initialPlayerInspectionState,
  );
  const playData = useMemo(() => (entity ? serializeContentUnit(entity) : null), [entity]);
  const inspectedStep = useMemo(
    () => getInspectedStep(playData, inspection.stepId),
    [inspection.stepId, playData],
  );
  const minimapHeight = clampMinimapHeight(
    preferredMinimapHeight ?? defaultMinimapHeight,
    maxMinimapHeight,
  );
  const { expandedCard, cardLookup } = useExpandedStepCard(containerRef, expandWorkflow ? null : inspection.stepId);
  const workflowStepIds = useMemo(() => recordArray(playData?.data.steps)
    .map((step) => toFiniteNumber(step.id)).filter((id): id is number => id !== null), [playData]);
  const expandedWorkflowIds = useMemo(() => workflowStepIds.filter((id) =>
    workflowOverrides.get(id) ?? workflowDefaultExpanded), [workflowStepIds, workflowOverrides, workflowDefaultExpanded]);
  const workflowVisible = expandWorkflow && mode === 'editable' && !graphCollapsed && bundleStatus === 'ready';
  const { cards: workflowCards, missing: missingWorkflowIds, settled: workflowLookupSettled } = useExpandedStepCards(containerRef, expandedWorkflowIds, workflowVisible);
  const singleStepHost = useRef<HTMLDivElement | null>(null);
  if (!singleStepHost.current) singleStepHost.current = document.createElement('div');
  const inspectionCards = useMemo(() => {
    const cards = new Map<number, ExpandedWorkflowCard>();
    if (expandedCard && inspection.stepId !== null) {
      singleStepHost.current!.dataset.authorStepHost = String(inspection.stepId);
      cards.set(inspection.stepId, { card: expandedCard, host: singleStepHost.current! });
    }
    return cards;
  }, [expandedCard, inspection.stepId]);
  const workflowContentHeight = useWorkflowDetailsLayer(containerRef, expandWorkflow ? workflowCards : inspectionCards,
    mode === 'editable' && !graphCollapsed, minimapSyncRef);
  const fitWorkflowContent = workflowVisible && workflowCards.size > 0;
  useEffect(() => {
    // Resume geometry publication after React removes the hidden-row attribute, even when
    // every panel is collapsed and no details-layer layout will request a sync of its own.
    if (!graphCollapsed) {
      pendingResizeRef.current = true;
      minimapSyncRef.current();
    }
  }, [graphCollapsed]);
  useEffect(() => {
    const controls = workflowControlsRef.current;
    if (!workflowVisible || !controls) return;
    const measure = () => setWorkflowControlsHeight(Math.max(40, controls.offsetHeight));
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(controls);
    return () => observer?.disconnect();
  }, [workflowVisible]);
  const closeManualStep = (stepId: number) => {
    if (manuallyOpenedSteps.current.delete(stepId)) recordActivitySignal({ kind: 'graph_step_collapsed', stepId });
  };
  const toggleWorkflowStep = (stepId: number) => {
    const next = !(workflowOverrides.get(stepId) ?? workflowDefaultExpanded);
    setWorkflowOverrides((previous) => new Map(previous).set(stepId, next));
    if (next) manuallyOpenedSteps.current.add(stepId);
    else manuallyOpenedSteps.current.delete(stepId);
    recordActivitySignal({ kind: next ? 'graph_step_expanded' : 'graph_step_collapsed', stepId });
  };
  workflowActionRef.current = (stepId, action) => {
    if (containerRef.current && findStepCards(containerRef.current).has(stepId)) toggleWorkflowStep(stepId);
    else {
      closeManualStep(stepId);
      dispatchInspection({ type: 'player-action', action, enabled: true });
    }
  };
  useEffect(() => {
    const opened = manuallyOpenedSteps.current;
    for (const id of opened) if (!workflowStepIds.includes(id)) closeManualStep(id);
  }, [workflowStepIds]);
  useEffect(() => {
    if (!expandWorkflow) return;
    const opened = manuallyOpenedSteps.current;
    return () => {
      for (const id of opened) recordActivitySignal({ kind: 'graph_step_collapsed', stepId: id });
      opened.clear();
    };
  }, [expandWorkflow]);

  /**
   * Reports that a step was opened for reading (owner decision 2026-08-06).
   *
   * This is the first *reading* signal the tool has ever had: every existing signal records a
   * decision somebody took, so `docs/reporting/DESIGN-activity-reporting.md` §1.7 could say what a
   * reviewer decided and never when they were working. A run of these bounds a working interval
   * that is otherwise invisible between two far-apart approvals.
   *
   * Fires on open only — a collapse sets `stepId` back to `null` and is not a second act of
   * reading. `recordActivitySignal` never throws and never blocks; if it cannot reach the server
   * the signal is dropped and the reviewer sees nothing.
   */
  const previousInspectedStep = useRef<number | null>(null);
  useEffect(() => {
    const previous = previousInspectedStep.current;
    previousInspectedStep.current = inspection.stepId;

    // A close is recorded as well as an open, so a stretch of reading has an OBSERVED end rather
    // than an inferred one. Without it the report has to guess how long a card stayed open, and a
    // guess is exactly what the presence overlay is trying to stop needing.
    if (previous !== null && previous !== inspection.stepId) {
      recordActivitySignal({ kind: 'graph_step_collapsed', stepId: previous });
    }
    if (inspection.stepId !== null) {
      recordActivitySignal({ kind: 'graph_step_expanded', stepId: inspection.stepId });
    }

    // Navigating away with a card still open is a close too. Without this the last stretch of
    // reading on every item has no observed end — which is the one thing the collapse signal
    // exists to provide.
    return () => {
      if (previousInspectedStep.current !== null) {
        recordActivitySignal({
          kind: 'graph_step_collapsed',
          stepId: previousInspectedStep.current,
        });
        previousInspectedStep.current = null;
      }
    };
  }, [inspection.stepId]);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) {
      return;
    }

    let widthBucket: number | null = null;
    const syncBounds = () => {
      // The review shell grows with the panels. Its fixed pane remains the resizer's authority:
      // using the growing shell here would inflate the ceiling on each ResizeObserver update.
      const frame = expandWorkflow ? shell.parentElement ?? shell : shell;
      const bounds = frame.getBoundingClientRect();
      const containerHeight = bounds.height || frame.clientHeight;
      // The vendor listens only for window resize, although a divider or full-screen toggle changes
      // its mount independently. Emit once per breakpoint crossing, never on every resize callback.
      if (bounds.width > 0) {
        const nextBucket = bounds.width <= 640 ? 0 : bounds.width <= 870 ? 1 : 2;
        const previousBucket = widthBucket;
        widthBucket = nextBucket;
        if (previousBucket !== null && previousBucket !== nextBucket) {
          if (mode === 'vanilla') {
            // Learner preview never mounts a minimap, so no graph publication will flush
            // this event. Update its vendor layout now; hidden editable graphs still defer.
            pendingResizeRef.current = false;
            window.dispatchEvent(new Event('resize'));
          } else pendingResizeRef.current = true;
        }
      }
      const nextMax = Math.max(
        MINIMAP_HEIGHT_MIN,
        Math.floor(containerHeight * MINIMAP_HEIGHT_MAX_RATIO),
      );
      const nextDefault = resolveDefaultMinimapHeight();
      setMaxMinimapHeight(nextMax);
      setDefaultMinimapHeight(nextDefault);
      minimapSyncRef.current();
    };

    syncBounds();
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(syncBounds)
      : null;
    resizeObserver?.observe(expandWorkflow ? shell.parentElement ?? shell : shell);
    window.addEventListener('resize', syncBounds);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncBounds);
    };
  }, [bundleStatus, entity, mode, expandWorkflow]);

  useEffect(() => {
    let cancelled = false;

    async function ensurePlayerBundle() {
      // The player stylesheet carries unlayered globals (e.g. `a{color:var(--ui-skin-link)}`)
      // that would beat every Tailwind utility once loaded — Tailwind 4 emits utilities inside
      // @layer, and unlayered author CSS wins over any layer. Import the player CSS into a
      // `player` layer declared before the app stylesheet's layers so ALL app styles —
      // including preflight — keep winning outside the player canvas (recorded defect:
      // S3-T7/S4-T8 dark-on-blue actions). The flip side is that Tailwind preflight would
      // also flatten the player canvas itself; the `@layer base` carve-out at the top of
      // PLAYER_OVERRIDE_CSS handles that half of the contract (2026-07-23 regression).
      if (!document.querySelector('style[data-player-preview="layer-order"]')) {
        const layerOrder = document.createElement('style');
        layerOrder.dataset.playerPreview = 'layer-order';
        layerOrder.textContent = '@layer player;';
        document.head.insertBefore(layerOrder, document.head.firstChild);
      }

      const existingCss = document.querySelector<HTMLStyleElement>('style[data-player-preview="css"]');
      if (!existingCss) {
        document.querySelector('link[data-player-preview="true"]')?.remove();
        const style = document.createElement('style');
        style.dataset.playerPreview = 'css';
        style.textContent = `@import url("${PLAYER_BASE}/${PLAYER_STYLE}") layer(player);`;
        document.head.appendChild(style);
      }

      const existingOverride = document.querySelector<HTMLStyleElement>(`style[data-player-preview="${PLAYER_OVERRIDE_STYLE_ID}"]`);
      if (!existingOverride) {
        const style = document.createElement('style');
        style.dataset.playerPreview = PLAYER_OVERRIDE_STYLE_ID;
        style.textContent = PLAYER_OVERRIDE_CSS;
        document.head.appendChild(style);
      }

      if (window.MindojoPlayer) {
        setBundleStatus('ready');
        return;
      }

      const existingScript = document.querySelector<HTMLScriptElement>('script[data-player-preview="true"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = `${PLAYER_BASE}/${PLAYER_SCRIPT}`;
        script.async = true;
        script.dataset.playerPreview = 'true';
        document.body.appendChild(script);

        await new Promise<void>((resolve, reject) => {
          const timeout = window.setTimeout(
            () => reject(new Error('Player bundle load timeout')),
            15_000,
          );
          script.onload = () => {
            window.clearTimeout(timeout);
            resolve();
          };
          script.onerror = () => {
            window.clearTimeout(timeout);
            reject(new Error('Player bundle failed to load'));
          };
        }).catch(() => undefined);
      } else if (!window.MindojoPlayer) {
        await new Promise<void>((resolve) => {
          const timeout = window.setTimeout(() => resolve(), 15_000);
          existingScript.addEventListener(
            'load',
            () => {
              window.clearTimeout(timeout);
              resolve();
            },
            { once: true },
          );
        });
      }

      if (!cancelled) {
        setBundleStatus(window.MindojoPlayer ? 'ready' : 'error');
      }
    }

    void ensurePlayerBundle();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!entity || !playData || bundleStatus !== 'ready' || !containerRef.current || !window.MindojoPlayer) {
      return;
    }

    playerRef.current?.destroy?.();
    containerRef.current.innerHTML = '';
    const editable = mode === 'editable'
      ? {
          editable: {
            is_dynamic_stage_enabled: false,
            is_answers_moderation_enabled: false,
            is_view_stats_enabled: false,
          },
        }
      : {};
    const player = new window.MindojoPlayer({
      elementId: containerRef.current.id,
      playData,
      appConfig: {
        course_id: '422',
        base_assets_url: PLAYER_BASE,
        ...(saveNamespace ? { namespace: saveNamespace } : {}),
      },
      playerConfig: {
        playerMode: mode,
        ignoreRequests: PLAYER_IGNORE_REQUESTS,
        isStandaloneMode: true,
        isTitleHidden: true,
        sessionType: 'entity',
        sessionItemId: entity.id,
        // The patch-ported player (2.2.0-jsx112.rc2+) reads this flag in place of
        // the old copy-player bundle patch: minimap node clicks must never drop
        // the author preview into edit mode.
        minimap: {
          is_click_to_edit_enabled: false,
        },
        ...editable,
      },
    });
    playerRef.current = player;

    const store = player.store;
    const minimapEnabled = mode === 'editable';
    const originalDispatch = store?.dispatch.bind(store);
    if (store && originalDispatch) {
      store.dispatch = (action: unknown) => {
        const stepId = getInspectionStepIdFromPlayerAction(action, minimapEnabled);
        if (stepId !== null) {
          if (expandWorkflow) workflowActionRef.current(stepId, action);
          else dispatchInspection({ type: 'player-action', action, enabled: true });
          if (import.meta.env.DEV) {
            console.debug('[PlayerPreview] Intercepted minimap jump for read-only inspection', {
              actionType: isRecord(action) ? action.type : undefined,
              stepId,
            });
          }
          return action;
        }

        return originalDispatch(action);
      };
    }

    dispatchInspection({ type: 'reset' });
    player.render?.();

    // The vendor starts closed. Opening it in a hidden row mounts connectors with 0/0
    // geometry, even before our own UPDATE guard runs. Show only from a visible sync.
    let minimapShown = false;

    let frameId: number | null = null;
    let activeScrollFrameId: number | null = null;

    const scheduleMinimapSync = () => {
      if (frameId != null) {
        return;
      }
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        syncMinimapViewport();
      });
    };
    minimapSyncRef.current = scheduleMinimapSync;

    const scheduleScrollActive = () => {
      if (activeScrollFrameId != null) {
        return;
      }
      activeScrollFrameId = window.requestAnimationFrame(() => {
        activeScrollFrameId = null;
        const root = containerRef.current;
        if (root) {
          scrollActiveNodeIntoView(root);
        }
      });
    };

    const syncMinimapViewport = () => {
      const root = containerRef.current;
      // Hide commits display:none before the details-layer cleanup requests its sync. A
      // queued store/resize callback can also arrive here after Hide. Publishing dimensions
      // then updates native connectors with 0/0 scale and starts their NaN setState loop.
      // Defer at execution time (not scheduling time); Show requests fresh visible geometry.
      if (!minimapEnabled || !store || !root || root.dataset.playerMinimap === 'hidden') {
        return;
      }

      if (!minimapShown) {
        const bounds = root.getBoundingClientRect();
        if (!(bounds.width > 0 && bounds.height > 0)) return;
        const row = root.querySelector<HTMLElement>('[class*="player__player-layout_contentLeft"]');
        if (row && !(row.clientWidth > 0 && row.clientHeight > 0)) return;
        minimapShown = true;
        store.dispatch({ type: PLAYER_SHOW_MINIMAP, payload: {} });
      }

      const minimapContainer = root.querySelector<HTMLElement>('[class*="player__minimap_container"]');
      const interactionViewport = minimapContainer?.firstElementChild;
      const levels = minimapContainer?.querySelector<HTMLElement>('[class*="player__minimap_levels"]');
      if (!(interactionViewport instanceof HTMLElement) || !levels) {
        return;
      }

      const viewportWidth = interactionViewport.clientWidth;
      const viewportHeight = interactionViewport.clientHeight;
      if (![viewportWidth, viewportHeight].every((value) => Number.isFinite(value) && value > 0)) return;
      clearStaleMinimapSize(levels);
      const contentWidth = levels.offsetWidth;
      const contentHeight = levels.offsetHeight;
      if (![contentWidth, contentHeight].every((value) => Number.isFinite(value) && value > 0)) return;
      const currentDimensions = store.getState().minimap?.mapDimensions;
      const nextDimensions = normalizeMinimapDimensions(currentDimensions, {
        viewportWidth,
        viewportHeight,
        contentWidth,
        contentHeight: contentHeight + (Number.parseFloat(levels.style.top) || 0),
      });

      if (!areMinimapDimensionsEqual(currentDimensions, nextDimensions)) {
        store.dispatch({
          type: PLAYER_UPDATE_MINIMAP,
          payload: {
            minimap: {
              mapDimensions: nextDimensions,
            },
          },
        });
      }
      if (pendingResizeRef.current) {
        pendingResizeRef.current = false;
        window.dispatchEvent(new Event('resize'));
      }
    };

    const unsubscribe = minimapEnabled ? (store?.subscribe(scheduleMinimapSync) ?? (() => undefined)) : () => undefined;
    const resizeObserver =
      minimapEnabled && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            scheduleMinimapSync();
          })
        : null;
    let previousActiveNode: HTMLElement | null = null;
    const mutationObserver =
      minimapEnabled && typeof MutationObserver !== 'undefined'
        ? new MutationObserver(() => {
            scheduleMinimapSync();
            // A class update or a replacement node can identify a new learner position. Portal
            // mutations retain the same active node and must not snap a manually panned graph back.
            const active = containerRef.current ? activeMinimapNode(containerRef.current) : null;
            if (active !== previousActiveNode) {
              previousActiveNode = active;
              scheduleScrollActive();
            }
          })
        : null;

    if (resizeObserver && containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    if (mutationObserver && containerRef.current) {
      mutationObserver.observe(containerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    if (minimapEnabled) {
      previousActiveNode = containerRef.current ? activeMinimapNode(containerRef.current) : null;
      scheduleMinimapSync();
      scheduleScrollActive();
    }

    return () => {
      unsubscribe();
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      if (frameId != null) {
        window.cancelAnimationFrame(frameId);
      }
      if (activeScrollFrameId != null) {
        window.cancelAnimationFrame(activeScrollFrameId);
      }
      if (minimapSyncRef.current === scheduleMinimapSync) {
        minimapSyncRef.current = () => undefined;
      }
      player.destroy?.();
      if (playerRef.current === player) {
        playerRef.current = null;
      }
    };
  }, [bundleStatus, entity, mode, playData, saveNamespace, expandWorkflow]);

  if (!entity) {
    return <div style={styles.placeholder}>Select an entity to preview it.</div>;
  }

  if (bundleStatus === 'error') {
    return <div style={styles.placeholder}>Player failed to load.</div>;
  }

  if (bundleStatus !== 'ready') {
    return <div style={styles.placeholder}>Loading player…</div>;
  }

  const shouldReserveAiPaneWidth = reserveAiPaneWidth ?? mode !== 'vanilla';
  // Reserve the header, vendor top translation, border and a native horizontal scrollbar.
  // Extra height belongs to the pane's scrollable document; it never steals the learner row.
  const fittedMinimapHeight = workflowContentHeight + workflowControlsHeight + MINIMAP_TOP_PADDING + 22;
  const shellStyle = {
    ...styles.shell,
    ...(fitWorkflowContent ? {
      height: `calc(100% + ${Math.max(0, fittedMinimapHeight - minimapHeight)}px)`,
      maxHeight: 'none',
    } : {}),
    '--author-workflow-controls-height': workflowVisible ? `${workflowControlsHeight}px` : '0px',
    '--author-ai-pane-width': shouldReserveAiPaneWidth
      ? 'calc(clamp(19rem, 23vw, 23rem) + 1rem)'
      : '0rem',
    // Review panels use their measured height. Other embeds retain the bounded single-step
    // inspection height; collapsed review graphs retain the original stored/default splitter.
    '--author-minimap-height':
      fitWorkflowContent
        ? `${Math.max(minimapHeight, fittedMinimapHeight)}px`
        : expandedCard !== null
          ? `${Math.min(Math.max(minimapHeight, MINIMAP_HEIGHT_EXPANDED_MIN), maxMinimapHeight)}px`
          : preferredMinimapHeight === null && minimapHeight === defaultMinimapHeight
            ? MINIMAP_HEIGHT_DEFAULT_CSS
            : `${minimapHeight}px`,
  } as CSSProperties;

  const updateMinimapHeight = (nextHeight: number) => {
    const clamped = Math.round(clampMinimapHeight(nextHeight, maxMinimapHeight));
    setPreferredMinimapHeight(clamped);
    writeStoredMinimapHeight(clamped);
    minimapSyncRef.current();
  };

  const resetMinimapHeight = () => {
    setPreferredMinimapHeight(null);
    removeStoredMinimapHeight();
    minimapSyncRef.current();
  };

  const handleSplitterKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    let nextHeight: number | null = null;
    switch (event.key) {
      case 'ArrowUp':
        nextHeight = minimapHeight + MINIMAP_HEIGHT_STEP;
        break;
      case 'ArrowDown':
        nextHeight = minimapHeight - MINIMAP_HEIGHT_STEP;
        break;
      case 'Home':
        nextHeight = MINIMAP_HEIGHT_MIN;
        break;
      case 'End':
        nextHeight = maxMinimapHeight;
        break;
      case 'Enter':
        event.preventDefault();
        resetMinimapHeight();
        return;
      default:
        return;
    }
    event.preventDefault();
    updateMinimapHeight(nextHeight);
  };

  const handleSplitterPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }
    dragRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startHeight: minimapHeight,
    };
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      // The pointer may have been cancelled before capture was established.
    }
    event.preventDefault();
  };

  const handleSplitterPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    updateMinimapHeight(drag.startHeight + drag.startY - event.clientY);
    event.preventDefault();
  };

  const finishSplitterDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) {
      return;
    }
    dragRef.current = null;
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch {
      // Capture may already be released by the browser on pointer cancellation.
    }
  };

  return (
    <div className="relative size-full min-h-0" ref={shellRef} style={shellStyle}>
      <div
        // Reveal the in-place edit actions only where saves are actually captured
        // (saveNamespace set). Editable embeds without a capture target (EntityEditor)
        // keep the player's hover-gated default so users aren't offered saves that 404.
        data-player-editable={mode === 'editable' && saveNamespace ? 'true' : undefined}
        data-player-review-workflow={expandWorkflow && mode === 'editable' ? 'true' : undefined}
        data-workflow-content-height={fitWorkflowContent ? 'natural' : undefined}
        data-workflow-lookup={workflowVisible ? workflowLookupSettled ? 'settled' : 'searching' : undefined}
        // Collapsed for the learner preview (there is no authoring graph there) and whenever the
        // reviewer has folded it away. Editing is unaffected either way — the previous only route
        // to a full-height item was Learner preview, which silently cost in-place editing too.
        data-player-minimap={mode !== 'editable' || graphCollapsed ? 'hidden' : undefined}
        id="player-preview-root"
        ref={containerRef}
        style={styles.canvas}
      />
      {mode === 'editable' ? (
        <div
          ref={workflowControlsRef}
          role={workflowVisible ? 'toolbar' : undefined}
          aria-label={workflowVisible ? 'Workflow graph controls' : undefined}
          className={workflowVisible ? 'absolute inset-x-0 z-30 flex flex-wrap items-center justify-between gap-2 px-2 py-2' : undefined}
          style={workflowVisible ? { bottom: 'calc(var(--author-minimap-height) - var(--author-workflow-controls-height))' } : undefined}
        >
          {workflowVisible ? (
            <button
              type="button"
              className="rounded-lg border border-slate-300 bg-white/95 px-2.5 py-1 text-[11px] font-bold text-slate-600 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-700"
              onClick={() => {
                if (expandedWorkflowIds.length === workflowStepIds.length) {
                  for (const id of [...manuallyOpenedSteps.current]) closeManualStep(id);
                }
                setWorkflowDefaultExpanded(expandedWorkflowIds.length !== workflowStepIds.length);
                setWorkflowOverrides(new Map());
                dispatchInspection({ type: 'return' });
              }}
            >
              {expandedWorkflowIds.length === workflowStepIds.length ? 'Collapse all' : 'Expand all'}
            </button>
          ) : null}
          <button
            aria-expanded={!graphCollapsed}
            className={`${workflowVisible ? '' : 'absolute bottom-2 right-2 z-30 '}rounded-lg border border-slate-300 bg-white/95 px-2.5 py-1 text-[11px] font-bold text-slate-600 shadow-sm hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700`}
            onClick={() => {
              setGraphCollapsed((collapsed) => {
                if (!expandWorkflow) writeStoredGraphCollapsed(!collapsed);
                return !collapsed;
              });
              // Showing or hiding the graph is navigation, not a decision: it says the reviewer is
              // working with the item, and it is the only evidence of that on an item they end up
              // leaving without acting on.
              recordActivitySignal({ kind: 'graph_toggled', stepId: null });
            }}
            title={graphCollapsed
              ? 'Show the workflow graph'
              : 'Hide the workflow graph and give the item its full height'}
            type="button"
          >
            {graphCollapsed ? 'Show workflow graph' : 'Hide workflow graph'}
          </button>
          {workflowVisible && missingWorkflowIds.length ? (
            <div className="flex max-h-24 w-full flex-wrap gap-2 overflow-auto rounded border border-amber-300 bg-white p-2 text-xs" aria-label="Workflow steps unavailable in graph">
              <span>Full text is available here:</span>
              {missingWorkflowIds.map((id) => <button key={id} type="button" className="rounded border border-slate-300 px-2 py-1"
                onClick={() => {
                  closeManualStep(id);
                  dispatchInspection({ type: 'player-action', action: { type: '@PLAYER/JUMP_TO', payload: { stepId: id, shouldStartEditing: false } }, enabled: true });
                }}>Read step {id}</button>)}
            </div>
          ) : null}
        </div>
      ) : null}
      {mode === 'editable' && !graphCollapsed && !fitWorkflowContent ? (
        <div
          aria-controls="player-preview-root"
          aria-label="Resize workflow graph"
          aria-orientation="horizontal"
          aria-valuemax={Math.round(maxMinimapHeight)}
          aria-valuemin={MINIMAP_HEIGHT_MIN}
          aria-valuenow={Math.round(minimapHeight)}
          aria-valuetext={`${Math.round(minimapHeight)} pixels`}
          className={`absolute inset-x-0 ${workflowVisible ? 'z-40' : 'z-20'} flex h-3 cursor-row-resize touch-none items-center px-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-emerald-700`}
          onDoubleClick={resetMinimapHeight}
          onKeyDown={handleSplitterKeyDown}
          onLostPointerCapture={() => {
            dragRef.current = null;
          }}
          onPointerCancel={finishSplitterDrag}
          onPointerDown={handleSplitterPointerDown}
          onPointerMove={handleSplitterPointerMove}
          onPointerUp={finishSplitterDrag}
          role="separator"
          style={{ bottom: 'calc(var(--author-minimap-height) - 6px)' }}
          tabIndex={0}
          title="Drag to resize the workflow graph. Press Enter to reset."
        >
          <span aria-hidden="true" className="h-1 w-full rounded-full bg-slate-400/80 shadow-sm" />
        </div>
      ) : null}
      {workflowVisible ? (
        <>
          {[...workflowCards].filter(([id]) => expandedWorkflowIds.includes(id)).map(([stepId, { host }]) => createPortal(
            <StepExpansion inspectedStep={getInspectedStep(playData, stepId)} stepId={stepId} onReturn={() => toggleWorkflowStep(stepId)} />,
            host, String(stepId),
          ))}
        </>
      ) : null}
      {/* Full text sits in our aligned layer, leaving native card/link geometry intact. */}
      {inspection.stepId !== null && expandedCard
        ? createPortal(
            <StepExpansion
              inspectedStep={inspectedStep}
              onReturn={() => dispatchInspection({ type: 'return' })}
              stepId={inspection.stepId}
            />,
            singleStepHost.current!,
          )
        : null}
      {inspection.stepId !== null && (expandWorkflow || cardLookup === 'absent') ? (
        <StepInspectionOverlay
          inspectedStep={inspectedStep}
          onReturn={() => dispatchInspection({ type: 'return' })}
          stepId={inspection.stepId}
        />
      ) : null}
    </div>
  );
}

interface InspectedResponse {
  warnings: string[];
  id: number;
  value: string;
  isCorrect: boolean | null;
  nextStepId: number | null;
}

interface InspectedStep {
  warnings: string[];
  prompt: string;
  nextStepId: number | null;
  responseFormat: string;
  responses: InspectedResponse[];
}

/** Substring selectors survive a re-vendor; parsing the entire number prevents 7 matching 70.
 * Ambiguous hooks are a missing lookup, never permission to put two steps in the same card. */
function directStepColumn(card: HTMLElement): HTMLElement | null {
  const column = card.closest<HTMLElement>('[class*="player__minimap-level_container"]');
  return column?.parentElement?.className.includes('player__minimap_levels') ? column : null;
}

// Old adapter mounts may have left a zero inline size. Clear it only after a positive
// measurement; never restore that invalid size when a full-text layer later cleans up.
function clearStaleMinimapSize(levels: HTMLElement) {
  const parent = levels.parentElement;
  if (!parent) return;
  for (const property of ['width', 'height'] as const) {
    if (parent.style[property] && Number.parseFloat(parent.style[property]) <= 0) parent.style[property] = '';
  }
  if (levels.style.top && Number.parseFloat(levels.style.top) <= 0) levels.style.top = '';
}

function findStepCards(root: HTMLElement): Map<number, HTMLElement> {
  const candidates = new Map<number, HTMLElement[]>();
  const owners = new Map<HTMLElement, Set<number>>();
  for (const anchor of root.querySelectorAll<HTMLElement>('[id*="minimapStepLinkEnd-"]')) {
    if (anchor.closest('[data-author-step-host]')) continue;
    const match = /minimapStepLinkEnd-(\d+)(?!\d)/.exec(anchor.id);
    const id = match ? Number(match[1]) : NaN;
    const card = anchor.closest<HTMLElement>('[class*="player__minimap-step_container"]');
    if (!Number.isFinite(id) || !card || !directStepColumn(card)) continue;
    candidates.set(id, [...candidates.get(id) ?? [], card]);
    owners.set(card, (owners.get(card) ?? new Set()).add(id));
  }
  const cards = new Map<number, HTMLElement>();
  for (const [id, matches] of candidates) {
    const card = matches[0]!;
    if (matches.length === 1 && owners.get(card)?.size === 1) cards.set(id, card);
  }
  return cards;
}

type ExpandedWorkflowCard = { card: HTMLElement; host: HTMLDivElement };

/** Full-text panels follow the vendor's columns, in a separate layer above its compact graph.
 * Move the whole native graph (including its SVG origin) together; none of its card/port offsets
 * change. Native connectors therefore stay correct even when memoized answer components skip
 * updates. Hosts remain ours when the vendor replaces nodes or clears a card body. */
function useWorkflowDetailsLayer(
  containerRef: { current: HTMLElement | null },
  cards: Map<number, ExpandedWorkflowCard>,
  enabled: boolean,
  syncRef: { current: () => void },
) {
  const [contentHeight, setContentHeight] = useState(0);
  useEffect(() => {
    setContentHeight(0);
    const root = containerRef.current;
    if (!root || !enabled || cards.size === 0) return;
    const minimap = root.querySelector<HTMLElement>('[class*="player__minimap_container"]');
    if (!minimap) return;
    const levels = minimap.querySelector<HTMLElement>('[class*="player__minimap_levels"]');
    const parent = levels?.parentElement ?? minimap;
    const layer = document.createElement('div');
    layer.dataset.authorWorkflowDetails = 'true';
    layer.setAttribute('aria-label', 'Workflow step details');
    Object.assign(layer.style, { display: 'grid', padding: '16px', position: 'relative' });
    const columns = levels ? [...levels.children].filter((node): node is HTMLElement =>
      node instanceof HTMLElement && node.className.includes('player__minimap-level_container')) : [];
    const groups = Array.from({ length: Math.max(1, columns.length) }, () => {
      const column = document.createElement('div');
      Object.assign(column.style, { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' });
      layer.appendChild(column);
      return column;
    });
    const ordered = [...cards.values()].sort((a, b) => a.card.compareDocumentPosition(b.card) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);
    for (const { card, host } of ordered) {
      const column = directStepColumn(card);
      const index = column ? columns.indexOf(column) : -1;
      if (index < 0) continue;
      Object.assign(host.style, { width: '300px', margin: '12px', padding: '12px', border: '1px solid #d97706',
        borderRadius: '10px', background: '#fff', cursor: 'text', overflowWrap: 'anywhere' });
      groups[index]!.appendChild(host);
    }
    const capturedTop = levels?.style.top ?? '';
    const previousTop = Number.parseFloat(capturedTop) <= 0 ? '' : capturedTop;
    const previousHeight = Number.parseFloat(parent.style.height) <= 0 ? '' : parent.style.height;
    const previousWidth = Number.parseFloat(parent.style.width) <= 0 ? '' : parent.style.width;
    parent.insertBefore(layer, levels ?? parent.firstChild);
    let frame = 0;
    const layout = () => {
      frame = 0;
      if (root.dataset.playerMinimap === 'hidden' || !levels) return;
      const viewport = minimap.firstElementChild;
      if (!(viewport instanceof HTMLElement)) return;
      // All inputs must be positive before writing any geometry into the vendor graph.
      if (![viewport.clientWidth, viewport.clientHeight].every((value) => Number.isFinite(value) && value > 0)) return;
      clearStaleMinimapSize(levels);
      const width = levels.offsetWidth;
      const height = levels.offsetHeight;
      const detailHeight = layer.offsetHeight;
      if (![width, height, detailHeight].every((value) => Number.isFinite(value) && value > 0)) return;
      // Offset dimensions ignore the vendor's pan/zoom transform, just like its connector math.
      const widths = columns.length ? columns.map((column) => column.offsetWidth || 324) : [324];
      layer.style.gridTemplateColumns = widths.map((width) => `${width}px`).join(' ');
      levels.style.top = `${detailHeight}px`;
      parent.style.height = `${detailHeight + height}px`;
      parent.style.width = `${width}px`;
      setContentHeight(detailHeight + height);
      syncRef.current();
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(layout); };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    observer?.observe(layer);
    if (levels) observer?.observe(levels);
    const ownershipObserver = new MutationObserver(() => {
      if (!root.contains(layer) && root.contains(parent)
        && (!levels || levels.parentElement === parent)) {
        parent.insertBefore(layer, levels ?? parent.firstChild);
        schedule();
      }
    });
    ownershipObserver.observe(parent, { childList: true });
    schedule();
    return () => {
      ownershipObserver.disconnect();
      observer?.disconnect();
      if (frame) cancelAnimationFrame(frame);
      layer.remove();
      if (levels) {
        levels.style.top = previousTop;
        parent.style.height = previousHeight;
        parent.style.width = previousWidth;
      }
      syncRef.current();
    };
  }, [containerRef, cards, enabled, syncRef]);
  return contentHeight;
}

/** Find each native card, but keep its full-text portal outside the vendor's geometry. */
function useExpandedStepCards(
  containerRef: { current: HTMLElement | null },
  stepIds: number[],
  enabled: boolean,
): { cards: Map<number, ExpandedWorkflowCard>; missing: number[]; settled: boolean } {
  const hosts = useRef(new Map<number, HTMLDivElement>());
  const [cards, setCards] = useState(new Map<number, ExpandedWorkflowCard>());
  const [missing, setMissing] = useState<number[]>([]);
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    const root = containerRef.current;
    if (!enabled || !root) {
      if (hosts.current.size && root) {
        for (const card of root.querySelectorAll('[data-author-step-expanded]')) card.removeAttribute('data-author-step-expanded');
      }
      for (const host of hosts.current.values()) host.remove();
      setCards((previous) => previous.size ? new Map() : previous);
      setMissing((previous) => previous.length ? [] : previous);
      setSettled(false);
      return;
    }
    let frame = 0;
    let attempts = 0;
    let cancelled = false;
    setSettled(false);
    const schedule = () => { if (!frame && !cancelled) frame = requestAnimationFrame(find); };
    const find = () => {
      frame = 0;
      if (cancelled) return;
      const found = findStepCards(root);
      const next = new Map<number, ExpandedWorkflowCard>();
      for (const id of stepIds) {
        const card = found.get(id);
        if (!card) continue;
        let host = hosts.current.get(id);
        if (!host) {
          host = document.createElement('div');
          host.dataset.authorStepHost = String(id);
          hosts.current.set(id, host);
        }
        next.set(id, { card, host });
      }
      // Collapsed cards release their host and styling, while the host remains ours to reuse.
      for (const [id, host] of hosts.current) {
        if (!next.has(id)) {
          host.remove();
        }
      }
      const retainedCards = new Set([...next.values()].map(({ card }) => card));
      // Collapsed or ambiguous cards release their highlight along with their detail panel.
      for (const card of root.querySelectorAll<HTMLElement>('[data-author-step-expanded]')) {
        if (!retainedCards.has(card)) card.removeAttribute('data-author-step-expanded');
      }
      for (const card of retainedCards) card.setAttribute('data-author-step-expanded', 'true');
      setCards((previous) => previous.size === next.size && [...next].every(([id, value]) =>
        previous.get(id)?.card === value.card && previous.get(id)?.host === value.host) ? previous : next);
      attempts += 1;
      setSettled(next.size === stepIds.length || attempts >= 30);
      const absent = attempts >= 30 ? stepIds.filter((id) => !next.has(id)) : [];
      setMissing((previous) => previous.length === absent.length && previous.every((id, index) => id === absent[index]) ? previous : absent);
      if (next.size !== stepIds.length && attempts < 30) schedule();
    };
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => !(mutation.target instanceof Element)
        || !mutation.target.closest('[data-author-step-host], [data-author-workflow-details]'))) schedule();
    });
    observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['id', 'class'] });
    schedule();
    return () => {
      cancelled = true;
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [containerRef, stepIds, enabled]);
  useEffect(() => {
    const ownedHosts = hosts.current;
    return () => {
      for (const host of ownedHosts.values()) {
        host.remove();
      }
      ownedHosts.clear();
    };
  }, []);
  return { cards, missing, settled };
}

/**
 * Finds the workflow-graph card for the open step, so its detail panel can align with its column.
 *
 * The graph is drawn by the vendored player, so the card is not ours to render — but it is ours to
 * find. Each card contains `<div id="minimapStepLinkEnd-<stepId>">`, which is a stable, unhashed
 * hook; `[class*=...]` gets us from there to the card itself without depending on the build-time
 * class hash.
 *
 * Two things make this defensive rather than trusting:
 *
 * - **It retries across frames.** The player re-renders the graph asynchronously after a jump, so
 *   the card frequently does not exist on the render that opened it.
 * - **It reports `absent` rather than failing silently.** If the id convention ever changes — a
 *   player upgrade, a content shape we have not seen — the caller falls back to the modal overlay
 *   that shipped before this, so the reviewer still gets the full text. A silent no-op would look
 *   exactly like a click that did nothing.
 */
function useExpandedStepCard(
  containerRef: { current: HTMLElement | null },
  stepId: number | null,
): { expandedCard: HTMLElement | null; cardLookup: 'idle' | 'searching' | 'found' | 'absent' } {
  const [expandedCard, setExpandedCard] = useState<HTMLElement | null>(null);
  const [cardLookup, setCardLookup] = useState<'idle' | 'searching' | 'found' | 'absent'>('idle');

  useEffect(() => {
    if (stepId === null) {
      setExpandedCard(null);
      setCardLookup('idle');
      return;
    }

    const root = containerRef.current;
    if (!root) {
      setCardLookup('absent');
      return;
    }

    setCardLookup('searching');
    let cancelled = false;
    let attempts = 0;
    let frame = 0;

    const find = () => {
      if (cancelled) return;
      const card = findStepCards(root).get(stepId) ?? null;
      if (card) {
        setExpandedCard(card);
        setCardLookup('found');
        return;
      }
      // No graph on the page at all — a collapsed strip, a mount that never rendered, or a test
      // harness with a stubbed player. There is nothing to wait for, so fall back at once rather
      // than spending half a second before showing the reviewer anything.
      if (!root.querySelector('[class*="player__minimap_container"]')) {
        setCardLookup('absent');
        return;
      }
      attempts += 1;
      // ~30 frames is half a second at 60Hz: long enough for the graph to redraw, short enough that
      // a reviewer who clicked something unresolvable is not left staring at nothing.
      if (attempts < 30) {
        frame = requestAnimationFrame(find);
        return;
      }
      setCardLookup('absent');
    };
    find();

    return () => {
      cancelled = true;
      if (frame) cancelAnimationFrame(frame);
    };
  }, [containerRef, stepId]);

  // Highlight only: native prompt and link points keep their vendor dimensions.
  useEffect(() => {
    if (!expandedCard) return;
    expandedCard.setAttribute('data-author-step-expanded', 'true');
    return () => expandedCard.removeAttribute('data-author-step-expanded');
  }, [expandedCard]);

  return { expandedCard, cardLookup };
}

/**
 * The opened step, rendered in an aligned full-text panel above the compact routing graph.
 *
 * Same data as `StepInspectionOverlay`. Review pages show all cards initially and the pane
 * scrolls over the full-height strip; other embeds retain bounded single-step inspection.
 * Native cards keep their dimensions in both cases.
 */
function StepExpansion({
  inspectedStep,
  onReturn,
  stepId,
}: {
  inspectedStep: InspectedStep | null;
  onReturn: () => void;
  stepId: number;
}) {
  return (
    <div className="mt-1 grid gap-2 border-t border-amber-300 pt-2 text-left" data-testid="graph-step-expansion"
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
      onWheel={(event) => event.stopPropagation()}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-amber-700">
          Step {stepId} — full text
        </span>
        <button
          className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] font-bold text-slate-600 hover:bg-slate-100"
          onClick={(event) => {
            // The card itself is a player control; without this the collapse click would also be
            // read as another jump and immediately re-open what we just closed.
            event.stopPropagation();
            onReturn();
          }}
          type="button"
        >
          Collapse
        </button>
      </div>
      {inspectedStep ? (
        <>
          <PanelText className="text-[13px] leading-5 text-slate-900" text={inspectedStep.prompt} />
          <FlatteningWarnings warnings={inspectedStep.warnings} />
          {inspectedStep.responses.length ? (
            <div aria-label="Responses" className="grid gap-1">
              {inspectedStep.responses.map((response) => (
                <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1" key={response.id}>
                  <PanelText className="text-[12px] leading-5 text-slate-900" text={response.value} />
                  <FlatteningWarnings warnings={response.warnings} />
                  {response.isCorrect !== null ? (
                    <span
                      className={`text-[10px] font-bold ${response.isCorrect ? 'text-emerald-700' : 'text-rose-700'}`}
                    >
                      {response.isCorrect ? 'Correct' : 'Incorrect'}
                    </span>
                  ) : null}
                  <div className="text-[11px] font-semibold text-slate-600">
                    {response.nextStepId === null ? 'Terminal response' : `Continues to step ${response.nextStepId}`}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[11px] font-semibold text-slate-600">
              {inspectedStep.nextStepId === null
                ? 'Terminal step'
                : `Continues to step ${inspectedStep.nextStepId}`}
            </div>
          )}
        </>
      ) : (
        <div className="text-[11px] text-amber-900">Step details are unavailable in this preview.</div>
      )}
    </div>
  );
}

function StepInspectionOverlay({
  inspectedStep,
  onReturn,
  stepId,
}: {
  inspectedStep: InspectedStep | null;
  onReturn: () => void;
  stepId: number;
}) {
  return (
    <div className="absolute inset-0 z-30 grid min-h-0 place-items-center overflow-auto bg-slate-950/35 p-4 sm:p-8">
      <section
        aria-labelledby="player-step-inspection-title"
        aria-modal="true"
        className="my-auto grid w-full max-w-3xl gap-5 rounded-2xl border border-amber-300 bg-white p-5 shadow-2xl sm:p-6"
        role="dialog"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-200 pb-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.14em] text-amber-700">Step {stepId}</div>
            <h2 className="text-lg font-bold text-slate-900" id="player-step-inspection-title">
              Inspecting step — read-only
            </h2>
          </div>
          <button
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
            onClick={onReturn}
            type="button"
          >
            Return to learner path
          </button>
        </div>

        {inspectedStep ? (
          <div className="grid gap-5 overflow-auto">
            <div className="grid gap-2">
              <div className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
                {inspectedStep.responseFormat.replaceAll('_', ' ')}
              </div>
              <PanelText className="text-base leading-7 text-slate-900" text={inspectedStep.prompt} />
              <FlatteningWarnings warnings={inspectedStep.warnings} />
            </div>

            {inspectedStep.responses.length ? (
              <div className="grid gap-2" aria-label="Responses">
                {inspectedStep.responses.map((response) => (
                  <div
                    className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
                    key={response.id}
                  >
                    <PanelText className="text-sm text-slate-900" text={response.value} />
                    <FlatteningWarnings warnings={response.warnings} />
                    <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
                      {response.isCorrect !== null ? (
                        <span className={response.isCorrect ? 'text-emerald-700' : 'text-rose-700'}>
                          {response.isCorrect ? 'Correct' : 'Incorrect'}
                        </span>
                      ) : null}
                      <span className="text-slate-600">
                        {response.nextStepId === null ? 'Terminal' : `Leads to step ${response.nextStepId}`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-600">
                {inspectedStep.nextStepId === null
                  ? 'Terminal step'
                  : `Continues to step ${inspectedStep.nextStepId}`}
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Step details are unavailable in this preview.
          </div>
        )}
      </section>
    </div>
  );
}

/** Plain review text must stay identical on first load and on portal remounts. The shipped
 * MathJax config/local.js ignores CodeMirror (it replaces the default tex2jax_ignore).
 * Opt out only these owned blocks, retaining LaTeX and the flattening warning. Reset the
 * editor class's box styles here; the vendor's learner views and MathJax config stay intact. */
function PanelText({ className, text }: { className: string; text: string }) {
  return <div className={`CodeMirror whitespace-pre-wrap ${className}`} data-author-panel-text
    style={{ height: 'auto', overflow: 'visible', position: 'static', background: 'transparent',
      fontFamily: 'inherit', direction: 'inherit', overflowWrap: 'anywhere' }}>{text}</div>;
}

function FlatteningWarnings({ warnings }: { warnings: string[] }) {
  return warnings.length ? <ul aria-label="Content display warnings" className="text-xs text-amber-900">
    {[...new Set(warnings)].map((warning) => <li key={warning}>{warning}</li>)}
  </ul> : null;
}

function getInspectedStep(
  playData: ReturnType<typeof serializeContentUnit> | null,
  stepId: number | null,
): InspectedStep | null {
  if (!playData || stepId === null) {
    return null;
  }

  const steps = recordArray(playData.data.steps);
  const step = steps.find((candidate) => toFiniteNumber(candidate.id) === stepId);
  if (!step) {
    return null;
  }

  const responseIds = new Set<number>();
  for (const group of recordArray(step.response_groups)) {
    for (const responseId of unknownArray(group.response_ids)) {
      const normalizedId = toFiniteNumber(responseId);
      if (normalizedId !== null) responseIds.add(normalizedId);
    }
  }

  for (const responseId of unknownArray(step.response_ids)) {
    const normalizedId = toFiniteNumber(responseId);
    if (normalizedId !== null) responseIds.add(normalizedId);
  }
  const prompt = renderContentText(step.prompt, { preserveParagraphs: true });
  const value = prompt.text ? prompt : renderContentText(step.value, { preserveParagraphs: true });
  const responseById = new Map(
    recordArray(playData.data.responses)
      .map((response) => [toFiniteNumber(response.id), response] as const)
      .filter((entry): entry is readonly [number, Record<string, unknown>] => entry[0] !== null),
  );

  return {
    prompt: value.text || 'Untitled step',
    warnings: [...prompt.warnings, ...value.warnings],
    nextStepId: toFiniteNumber(step.next_step_id),
    responseFormat: toDisplayString(step.response_format, 'content'),
    responses: [...responseIds].map((responseId) => {
      const response = responseById.get(responseId);
      const rendered = renderContentText(response?.value, { preserveParagraphs: true });
      return {
        id: responseId,
        value: rendered.text || `Response ${responseId}`,
        warnings: rendered.warnings,
        isCorrect: typeof response?.is_correct === 'boolean' ? response.is_correct : null,
        nextStepId: toFiniteNumber(response?.next_step_id),
      };
    }),
  };
}

function recordArray(value: unknown): Record<string, unknown>[] {
  return unknownArray(value).filter(isRecord);
}

function unknownArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function toDisplayString(value: unknown, fallback: string): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return fallback;
}

function resolveDefaultMinimapHeight() {
  const viewportHeight = typeof window === 'undefined' ? 0 : window.innerHeight;
  return Math.min(
    MINIMAP_HEIGHT_DEFAULT_MAX,
    Math.max(MINIMAP_HEIGHT_DEFAULT_MIN, viewportHeight * 0.2),
  );
}

function clampMinimapHeight(height: number, maximum: number) {
  return Math.min(Math.max(height, MINIMAP_HEIGHT_MIN), Math.max(MINIMAP_HEIGHT_MIN, maximum));
}

function readStoredGraphCollapsed() {
  try {
    return window.sessionStorage.getItem(GRAPH_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeStoredGraphCollapsed(collapsed: boolean) {
  try {
    window.sessionStorage.setItem(GRAPH_COLLAPSED_STORAGE_KEY, String(collapsed));
  } catch {
    // A blocked or full session store must not disable the toggle.
  }
}

function readStoredMinimapHeight() {
  try {
    const stored = window.sessionStorage.getItem(MINIMAP_HEIGHT_STORAGE_KEY);
    if (stored === null) {
      return null;
    }
    const height = Number(stored);
    return Number.isFinite(height) ? height : null;
  } catch {
    return null;
  }
}

function writeStoredMinimapHeight(height: number) {
  try {
    window.sessionStorage.setItem(MINIMAP_HEIGHT_STORAGE_KEY, String(Math.round(height)));
  } catch {
    // A blocked or full session store must not disable resizing.
  }
}

function removeStoredMinimapHeight() {
  try {
    window.sessionStorage.removeItem(MINIMAP_HEIGHT_STORAGE_KEY);
  } catch {
    // A blocked session store must not disable reset.
  }
}

const styles: Record<string, CSSProperties> = {
  placeholder: {
    color: '#496055',
    height: '100%',
    minHeight: '100%',
  },
  shell: {
    height: '100%',
    minHeight: '100%',
    maxHeight: '100%',
    width: '100%',
    borderRadius: '18px',
    background: '#f4f0ea',
    overflow: 'hidden',
    position: 'relative',
  },
  canvas: {
    height: '100%',
    minHeight: '100%',
    maxHeight: '100%',
    width: '100%',
    background: '#f4f0ea',
    overflow: 'hidden',
    position: 'relative',
  } as CSSProperties,
};
